import { request } from '@umijs/max';

// ---- Call wrap-up (after-call work) API ----
// Backend: Controllers/api/CallWrapup/CallWrapupApiController.php
// All endpoints are TenantAuth; the SID may be either the CSR (child) leg or
// the parent leg — the backend resolves it to the call-log row.

export type WrapupNextActionType = null | 'callback' | 'escalation';
export type WrapupChannel = 'voice' | 'sms';

export type WrapupOutcome = {
  id: number;
  code: string;
  label: string;
  color: string | null;
  sort_order: number;
  is_active: boolean;
  requires_notes: boolean;
  next_action_type: WrapupNextActionType;
  default_callback_due_minutes: number | null;
  allowed_channels: WrapupChannel[];
  acw_seconds: number | null;
};

export type WrapupSettings = {
  enabled: boolean;
  acw_seconds_default: number;
  exception_after_minutes: number;
  appt_queue_id: string | null;
  appt_queue_name: string | null;
};

export type WrapupConfig = {
  settings: WrapupSettings;
  outcomes: WrapupOutcome[];
};

export type WrapupStatus = 'open' | 'saved' | 'expired';

// Datetimes are server-local 'YYYY-MM-DD HH:mm:ss' strings. Use `server_time`
// (stamped on every payload) to compute countdowns as an offset so client
// clock skew does not matter.
export type Wrapup = {
  id: number;
  call_sid: string;
  customer_call_sid: string | null;
  status: WrapupStatus;
  agent_user_id: number;
  customer_phone: string | null;
  dispatch_id: number | null;
  outcome_code: string | null;
  outcome_label: string | null;
  notes: string | null;
  call_ended_at: string;
  wrapup_started_at: string;
  wrapup_ends_at: string;
  wrapup_completed_at: string | null;
  wrapup_completed_reason: string | null;
  exception_due_at: string;
  crm_writeback_status: string;
  follow_up_due_at: string | null;
  escalation_id: number | null;
  callback_queue_assignment_id: number | null;
  saved_at: string | null;
  // CSR-leg call log and whether its Twilio recording is available yet
  // (stream: tenant-admin/live-calls/recordings/{twilio_call_log_id}/audio).
  twilio_call_log_id: number | null;
  has_recording: boolean;
  server_time: string;
};

export type WrapupSaveFieldErrors = {
  outcome_code?: 'required' | 'unknown' | 'not_allowed_for_channel';
  notes?: 'required' | 'too_long';
};

// Body of the 422 the save endpoint returns on validation failure.
export type WrapupSaveValidationError = {
  success: false;
  error: 'validation_failed';
  message: string;
  fields: WrapupSaveFieldErrors;
};

export async function getCallWrapupConfig(): Promise<{ success: boolean; data: WrapupConfig }> {
  return request('/api/tenant/call-wrapups/config', {
    method: 'GET',
  });
}

export async function openCallWrapup(
  call_sid: string,
): Promise<{ success: boolean; data: { applicable: boolean; reason?: string; wrapup?: Wrapup } }> {
  return request('/api/tenant/call-wrapups/open', {
    method: 'POST',
    data: { call_sid },
  });
}

export async function getCallWrapupByCallSid(
  call_sid: string,
): Promise<{ success: boolean; data: { wrapup: Wrapup | null } }> {
  return request(`/api/tenant/call-wrapups/by-call-sid/${encodeURIComponent(call_sid)}`, {
    method: 'GET',
  });
}

// The agent's still-open wrap-ups — used on widget mount to recover a wrap-up
// that was interrupted by a page refresh.
export async function getPendingCallWrapups(): Promise<{ success: boolean; data: { wrapups: Wrapup[] } }> {
  return request('/api/tenant/call-wrapups/pending', {
    method: 'GET',
  });
}

export type SaveCallWrapupPayload = {
  call_sid: string;
  outcome_code: string;
  notes: string;
  // ISO string; only meaningful for outcomes with next_action_type 'callback'.
  callback_due_at?: string;
};

// Errors are NOT routed through the global umi error handler: the widget
// renders the 422 `message` inline and must keep the popup open, and the
// 403/404 bodies are surfaced the same way. Callers read
// `err.response.status` / `err.response.data`.
export async function saveCallWrapup(
  data: SaveCallWrapupPayload,
): Promise<{ success: true; data: { wrapup: Wrapup } }> {
  return request('/api/tenant/call-wrapups/save', {
    method: 'POST',
    data,
    skipErrorHandler: true,
  });
}
